import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const merge = (saved, columns) => {
  const known = new Set(columns.map((c) => c.key));
  const kept = (saved || []).filter((s) => known.has(s.key));
  const have = new Set(kept.map((s) => s.key));
  const out = [...kept, ...columns.filter((c) => !have.has(c.key)).map((c) => ({ key: c.key, visible: true, width: null }))];
  return out.some((c) => c.visible) ? out : out.map((c, i) => ({ ...c, visible: i === 0 }));
};
const defaults = (columns) => columns.map((c) => ({ key: c.key, visible: true, width: null }));

// Per-user column layout (order, visibility, width) saved on the server per tableId; null tableId = off
export function useTablePrefs(tableId, columns) {
  const [saved, setSaved] = useState(null);
  const timer = useRef(null);
  const pending = useRef(null);
  useEffect(() => {
    if (!tableId) { return undefined; }
    let live = true;
    api.get(`/me/table-prefs/${tableId}`).then((r) => { if (live) { setSaved(r.data.columns || []); } }).catch(() => { if (live) { setSaved([]); } });
    return () => { live = false; };
  }, [tableId]);
  useEffect(() => () => {
    clearTimeout(timer.current);
    if (pending.current) { api.put(`/me/table-prefs/${pending.current.id}`, { columns: pending.current.cols }).catch(() => {}); }
  }, []);
  const layout = useMemo(() => (tableId ? merge(saved, columns) : null), [tableId, saved, columns]);
  const setLayout = useCallback((next) => {
    setSaved(next);
    clearTimeout(timer.current);
    pending.current = { id: tableId, cols: next };
    timer.current = setTimeout(() => {
      pending.current = null;
      api.put(`/me/table-prefs/${tableId}`, { columns: next }).catch(() => toast.error("Column layout not saved - kept for this session"));
    }, 700);
  }, [tableId]);
  const reset = useCallback(() => {
    clearTimeout(timer.current);
    pending.current = null;
    setSaved(defaults(columns));
    api.delete(`/me/table-prefs/${tableId}`).catch(() => toast.error("Could not reset the saved layout"));
  }, [tableId, columns]);
  return { layout, setLayout, reset, ready: !tableId || saved !== null };
}
